/**
 * Auth context. Holds the signed-in operator's identity (never their MCP credential —
 * that stays server-side in the BFF session). On mount it probes /api/auth/me to restore an
 * existing session; login/logout flip the state and the route guard reacts.
 *
 * SIGNING IN IS TWO STEPS, since 2026-09-03. Mike: "all staff accounts must use a passkey."
 * A correct password no longer opens a session; it opens a passkey ceremony — registration
 * when the account has no passkey yet, authentication when it has one — and only the
 * completed ceremony issues the session. `login()` runs both steps and settles only when the
 * BFF has issued the session, or throws with the reason it did not. The Login page shows what
 * is happening between the two.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { api, ApiError } from './api';

export interface Identity {
  userId: number;
  roleId: number;
  email: string;
  displayName: string | null;
  method: string;
}

export type LoginPhase = 'password' | 'enroll' | 'passkey';

interface AuthState {
  identity: Identity | null; // null = signed out
  loading: boolean; // initial /me probe in flight
  /** Runs password + passkey. `onPhase` reports which step the user is on, for the page to explain. */
  login: (email: string, password: string, onPhase?: (phase: LoginPhase) => void) => Promise<void>;
  logout: () => Promise<void>;
}

type LoginStart =
  | { authenticated: true; identity: Identity }
  | { authenticated: false; step: 'enroll'; challengeId: string; options: PublicKeyCredentialCreationOptionsJSON; message?: string }
  | { authenticated: false; step: 'passkey'; challengeId: string; options: PublicKeyCredentialRequestOptionsJSON };

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  // Whose data the query cache was filled for. A ref rather than state because it has to
  // outlive the identity going null: an expired session lands on the login page without
  // logout() running, and the next sign-in still needs to know who was here before.
  const cachedFor = useRef<number | null>(null);

  /**
   * Adopt a signed-in identity. The query cache holds whatever the last operator's role was
   * allowed to read, under keys that do not name them, so a different operator signing in to
   * the same tab would be shown it at once from cache - and kept on it if their own refetch was
   * refused. Cleared BEFORE the identity changes, so no render pairs the new operator with the
   * old operator's data.
   */
  const adopt = useCallback(
    (next: Identity) => {
      if (cachedFor.current !== null && cachedFor.current !== next.userId) queryClient.clear();
      cachedFor.current = next.userId;
      setIdentity(next);
    },
    [queryClient],
  );

  useEffect(() => {
    let active = true;
    api<{ identity: Identity }>('/auth/me')
      .then((r) => active && adopt(r.identity))
      .catch(() => active && setIdentity(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [adopt]);

  const login = useCallback(async (email: string, password: string, onPhase?: (phase: LoginPhase) => void) => {
    onPhase?.('password');
    const start = await api<LoginStart>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (start.authenticated) {
      adopt(start.identity);
      return;
    }
    if (start.step === 'enroll') {
      onPhase?.('enroll');
      let response;
      try {
        response = await startRegistration({ optionsJSON: start.options });
      } catch (err) {
        // The browser refused, the user cancelled, or no authenticator exists. Nothing was
        // stored and no session exists: Mike's rule, "if they do not successfully create that
        // passkey then the login doesn't work."
        throw new ApiError(401, `A passkey is required for staff accounts and it was not created (${describe(err)}). Start again to try once more.`, 'passkey_not_created');
      }
      const done = await api<{ identity: Identity }>('/auth/passkey/register', {
        method: 'POST',
        body: JSON.stringify({ challengeId: start.challengeId, response, name: deviceName() }),
      });
      adopt(done.identity);
      return;
    }
    onPhase?.('passkey');
    let response;
    try {
      response = await startAuthentication({ optionsJSON: start.options });
    } catch (err) {
      throw new ApiError(401, `Your passkey was not confirmed (${describe(err)}). Start again.`, 'passkey_rejected');
    }
    const done = await api<{ identity: Identity }>('/auth/passkey/authenticate', {
      method: 'POST',
      body: JSON.stringify({ challengeId: start.challengeId, response }),
    });
    adopt(done.identity);
  }, [adopt]);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      // Cleared even when the logout call failed: the operator is leaving either way, and the
      // next person at this tab must not inherit what they were looking at.
      queryClient.clear();
      cachedFor.current = null;
      setIdentity(null);
    }
  }, [queryClient]);

  return <AuthContext.Provider value={{ identity, loading, login, logout }}>{children}</AuthContext.Provider>;
}

function describe(err: unknown): string {
  if (err && typeof err === 'object' && 'name' in err && (err as { name?: string }).name === 'NotAllowedError') return 'cancelled or timed out';
  if (err instanceof Error && err.message) return err.message;
  return 'the browser did not complete it';
}

/** A label for the passkey row, so a settings screen can tell a phone from a laptop. */
function deviceName(): string {
  const ua = navigator.userAgent;
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : 'device';
  return `${os} · ${new Date().toISOString().slice(0, 10)}`;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
